import React from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardList, User } from "lucide-react";
import { RecentItem } from "@/lib/utils/recents";

interface RecentsStripProps {
	items: RecentItem[];
	pageIcon: (key: string) => React.ReactNode;
}

const TYPE_STYLES: Record<RecentItem["type"], string> = {
	page: "bg-slate-50 border-slate-200/80 text-slate-700",
	client: "bg-amber-50 border-amber-200/80 text-amber-800",
	vendor: "bg-emerald-50 border-emerald-200/80 text-emerald-800",
	"job-request-form": "bg-indigo-50 border-indigo-200/80 text-indigo-800",
};

const COLORS = [
	"#950952", "#5E0035", "#023618", "#005C69", "#BF8B85",
	"#AD8A64", "#721121", "#A5402D", "#A49E8D", "#504136",
	"#689689", "#272838", "#5D536B", "#7D6B91", "#347FC4",
	"#B98B82", "#37515F", "#9899A6", "#AA78A6", "#824670",
	"#A2708A", "#629460",
];

const getVendorInitials = (name: string): string =>
	name
		.split(" ")
		.map((w) => w.charAt(0))
		.slice(0, 2)
		.join("")
		.toUpperCase() || "?";

const getVendorColor = (name: string): string => {
	let hash = 0;
	for (let i = 0; i < name.length; i++) {
		hash = name.charCodeAt(i) + ((hash << 5) - hash);
	}
	return COLORS[Math.abs(hash % COLORS.length)];
};

const getPillKey = (item: RecentItem): string => {
	switch (item.type) {
		case "page": return item.key;
		case "client": return item.id;
		case "vendor": return item.orgId;
		case "job-request-form": return `${item.vendorOrgId}:${item.formId}`;
	}
};

const getPillContent = (item: RecentItem, pageIcon: (key: string) => React.ReactNode) => {
	switch (item.type) {
		case "page":
			return {
				label: item.title,
				path: item.path,
				icon: (
					<span className="w-4 h-4 flex items-center justify-center [&_svg]:w-4 [&_svg]:h-4">
						{pageIcon(item.key)}
					</span>
				),
			};
		case "client":
			return {
				label: item.name,
				path: `/client/${item.id}`,
				icon: <User className="w-4 h-4 text-amber-500" />,
			};
		case "vendor": {
			const initials = getVendorInitials(item.name);
			const bg = getVendorColor(item.name);
			return {
				label: item.name,
				path: `/vendor/${item.orgId}`,
				icon: (
					<div
						className="w-5 h-5 rounded-md flex items-center justify-center text-white font-semibold"
						style={{ backgroundColor: bg, fontSize: "10px", lineHeight: 1 }}
					>
						{initials}
					</div>
				),
			};
		}
		case "job-request-form":
			return {
				label: `${item.formTitle} – ${item.vendorName}`,
				path: `/vendor/${item.vendorOrgId}/job-request?formId=${item.formId}`,
				icon: <ClipboardList className="w-4 h-4 text-indigo-500" />,
			};
	}
};

export const RecentsStrip: React.FC<RecentsStripProps> = ({ items, pageIcon }) => {
	const navigate = useNavigate();

	if (items.length === 0) return null;

	return (
		<div
			className="px-3 pt-3 pb-1 flex items-center flex-wrap gap-2 flex-shrink-0 overflow-hidden max-h-[48px]"
			data-testid="dashboard-recents"
		>
			<span className="text-xs text-slate-400 font-medium mr-0.5">Zuletzt</span>
			{items.map((item) => {
				const pill = getPillContent(item, pageIcon);
				return (
					<button
						key={getPillKey(item)}
						onClick={() => navigate(pill.path)}
						className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border shadow-sm hover:shadow hover:-translate-y-[1px] transition-all duration-150 text-xs font-medium ${TYPE_STYLES[item.type]}`}
						aria-label={`Navigiere zu ${pill.label}`}
					>
						{pill.icon}
						{pill.label}
					</button>
				);
			})}
		</div>
	);
};
